import { useMemo } from 'react';
import { EmailActionPage } from '../features/auth/EmailActionPage.jsx';
import { GroupLandingScreen } from '../screens/GroupLandingScreen.jsx';
import { GroupVoteScreen } from '../screens/GroupVoteScreen.jsx';
import { LandingScreen } from '../screens/LandingScreen.jsx';
import { isHostLaunch, readInvites } from '../lib/messagesBridge.js';

/**
 * Root of the web build.
 *
 * The web app has one job beyond marketing: let someone who was invited to a
 * group vote from whatever phone they already have, without installing
 * anything. Everything else — discovery, saved places, accounts, membership —
 * lives in the iOS app.
 *
 * Three states, still no router. An invite code wins over everything, because
 * someone arriving with one is here to do something rather than to read.
 * Otherwise the path picks which pitch to make.
 *
 * A path rather than a query parameter for `/groups`, because it is going on
 * posters and into group chats and has to be sayable out loud. Nothing extra is
 * needed to serve it: Express falls through to index.html for any non-API path
 * (see `serveClient` in server/src/app.js) and Vite does the same in dev.
 */
export function App() {
  const route = useMemo(readRoute, []);

  if (route.emailAction) {
    return <EmailActionPage action={route.emailAction} />;
  }

  if (route.groupCode) {
    return (
      <GroupVoteScreen
        code={route.groupCode}
        embedded={route.embedded}
        hosting={route.hosting}
        invites={route.invites}
        setupMode={route.setupMode}
      />
    );
  }
  return route.isGroupPitch ? <GroupLandingScreen /> : <LandingScreen />;
}

/**
 * Where this visit should land, read once from the URL.
 *
 * Four of these come from the iMessage extension, which hosts this page in a
 * web view rather than reimplementing the deck in Swift. It knows things the
 * page cannot work out for itself — that it is inside a message sheet, who this
 * person is, and, when a group is being restarted for people the app already
 * remembers, who was invited. See client/src/lib/messagesBridge.js and
 * mobile/targets/imessage.
 */
function readRoute() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('group');
  const embedded = params.get('embed') === '1';
  const hosting = embedded && isHostLaunch(params.get('host'));

  return {
    groupCode: code?.trim() || null,
    // Drops the header and the footer. There is no room for a wordmark in a
    // message sheet, and nowhere for "get the app" to open.
    embedded,
    // A group started from the iMessage extension may have no invite roster
    // yet. It still needs to create the session and join the sender.
    hosting,
    setupMode: hosting && params.get('setup') === 'pick' ? 'pick' : 'random',
    invites: readInvites(params.get('invite')),
    // The `me` and `name` parameters the extension also sends are read by
    // `lib/identity.js`, where the answers to "who is this" already live.
    // Trailing slash tolerated, so `/groups` and `/groups/` are the same page
    // and neither one 404s in someone's slide deck.
    isGroupPitch: /^\/groups\/?$/.test(window.location.pathname),
    // Fallback landing for email links when iOS Universal Links don't intercept
    // (desktop browser, or iPhone without the app installed).
    emailAction: /^\/(verify-email|reset-password)\/?$/.test(window.location.pathname)
      ? window.location.pathname.replace(/^\/|\/$/g, '')
      : null,
  };
}
